/**
 * Genera un PDF A4 con grid 4×6 de etiquetas (24 por página).
 *
 * Cada etiqueta lleva DataMatrix GS1 + producto + lote + vencimiento + n° serie.
 * Renderizado con jsPDF; el DataMatrix lo dibuja bwip-js sobre un canvas off-screen
 * y se embebe como PNG.
 *
 * Decisión: 4 columnas × 6 filas = 24 etiquetas por A4 (75×40 mm aprox).
 * Es un compromiso razonable entre legibilidad y densidad. Si el operador
 * necesita etiquetas más chicas/grandes, en un futuro este módulo expone
 * parámetros (ancho/alto/cols/rows).
 */

import { construirStringGs1 } from '@/domain/gs1';
import type { Serie } from '@/api/contracts/series';
import type { Producto } from '@/api/contracts/productos';

interface GenerarPdfEtiquetasInput {
  producto: Producto;
  series: Serie[];
  nombreArchivo: string;
}

// Grid 4×6 sobre A4 (210×297 mm). Márgenes: 10mm. Etiqueta: 47×42mm aprox.
const COLS = 4;
const ROWS = 6;
const MARGEN_X = 10;
const MARGEN_Y = 10;
const ANCHO_ETIQUETA = 47;
const ALTO_ETIQUETA = 42;
const ESPACIO_X = 1;
const ESPACIO_Y = 4;

export async function generarPdfEtiquetas({
  producto,
  series,
  nombreArchivo,
}: GenerarPdfEtiquetasInput): Promise<void> {
  if (series.length === 0) return;

  // Code-splitting: jsPDF y bwip-js suman ~600KB minified. Cargar solo cuando
  // el usuario realmente exporte un PDF — la SPA inicial queda chica.
  const [{ default: jsPDF }, bwipjsModule] = await Promise.all([
    import('jspdf'),
    import('bwip-js/browser'),
  ]);
  const bwipjs = bwipjsModule.default;

  const pdf = new jsPDF({ unit: 'mm', format: 'a4' });
  const porPagina = COLS * ROWS;

  for (let i = 0; i < series.length; i++) {
    const serie = series[i];
    if (!serie) continue;
    if (i > 0 && i % porPagina === 0) {
      pdf.addPage();
    }
    const indexEnPagina = i % porPagina;
    const col = indexEnPagina % COLS;
    const row = Math.floor(indexEnPagina / COLS);
    const x = MARGEN_X + col * (ANCHO_ETIQUETA + ESPACIO_X);
    const y = MARGEN_Y + row * (ALTO_ETIQUETA + ESPACIO_Y);

    // DataMatrix canvas off-screen
    const canvas = document.createElement('canvas');
    const stringGs1 = construirStringGs1({
      gtin: serie.gtin,
      lote: serie.lote,
      vencimiento: serie.vencimiento,
      serieId: serie.id,
    });
    try {
      bwipjs.toCanvas(canvas, {
        bcid: 'gs1datamatrix',
        text: stringGs1,
        scale: 4,
        paddingwidth: 5,
        paddingheight: 5,
        parsefnc: true,
      });
    } catch (err) {
      // Si falla, etiqueta queda sin barcode (mejor que abortar todo el PDF)
      console.error('bwip-js error en serie', serie.id, err);
    }
    const dmDataUrl = canvas.toDataURL('image/png');

    // Caja con borde
    pdf.setDrawColor(180, 180, 180);
    pdf.setLineWidth(0.2);
    pdf.rect(x, y, ANCHO_ETIQUETA, ALTO_ETIQUETA);

    // Producto (header)
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(7);
    pdf.setTextColor(30, 40, 70);
    pdf.text(producto.nombre, x + 2, y + 4, { maxWidth: ANCHO_ETIQUETA - 4 });
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(5);
    pdf.setTextColor(120, 120, 120);
    pdf.text(producto.presentacion, x + 2, y + 7, { maxWidth: ANCHO_ETIQUETA - 4 });

    // DataMatrix
    const sizeDm = 22; // mm
    pdf.addImage(
      dmDataUrl,
      'PNG',
      x + (ANCHO_ETIQUETA - sizeDm) / 2,
      y + 9,
      sizeDm,
      sizeDm,
    );

    // Texto legible debajo
    pdf.setFont('courier', 'normal');
    pdf.setFontSize(5);
    pdf.setTextColor(80, 80, 80);
    let ty = y + 34;
    pdf.text(`GTIN: ${serie.gtin}`, x + 2, ty);
    ty += 2;
    pdf.text(`Lote: ${serie.lote}  Vto: ${serie.vencimiento}`, x + 2, ty);
    ty += 2;
    pdf.setFont('courier', 'bold');
    pdf.setTextColor(30, 40, 70);
    pdf.text(`S/N: ${serie.id}`, x + 2, ty);
  }

  pdf.save(nombreArchivo);
}
